import { type Category, IVA_RATES, type Product, tierPrice } from '@facture/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BoxIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  TagsIcon,
  Trash2Icon,
  WrenchIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { CategoriesDialog } from '@/components/forms/categories-dialog'
import { ImportExportMenu } from '@/components/forms/import-export-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect, FilterTabs } from '@/components/ui/filter-bar'
import { Tooltip } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { categoriesQuery, productsQuery, useSetProductActive } from '@/features/products/api'
import { ProductFormSheet } from '@/features/products/product-form-sheet'
import { ProductImportDialog } from '@/features/products/product-import-dialog'
import { ApiRequestError, api } from '@/lib/api'
import { formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'

const searchSchema = z.object({
  q: z.string().optional(),
  kind: z.enum(['good', 'service']).optional(),
  category: z.string().optional(),
  status: z.enum(['active', 'archived']).optional(),
  page: z.number().int().min(1).optional(),
})

export const Route = createFileRoute('/_app/products')({
  staticData: { crumb: 'Productos' },
  validateSearch: searchSchema,
  component: ProductsPage,
})

const col = columnHelper<Product>()

function ProductsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCan('products:write')
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [editing, setEditing] = useState<Product | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const setActive = useSetProductActive()
  const queryClient = useQueryClient()

  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const list = useQuery(
    productsQuery({
      search: search.q,
      kind: search.kind,
      categoryId: search.category,
      status: search.status ?? 'active',
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const categories = useQuery(categoriesQuery())
  // La exportación respeta la búsqueda y los filtros vigentes de la lista.
  const exportParams = new URLSearchParams(
    Object.entries({
      search: search.q,
      kind: search.kind,
      categoryId: search.category,
      status: search.status ?? 'active',
    }).flatMap(([k, v]) => (v ? [[k, v]] : [])),
  ).toString()

  /** Solo se borra un producto nunca usado en un comprobante o cotización; si no, se sugiere archivarlo. */
  const removeProduct = useCallback(
    (p: Product) =>
      confirm({
        title: `¿Eliminar “${p.name}”?`,
        description: 'Esta acción no se puede deshacer. Si ya se usó en comprobantes, archivalo en su lugar.',
        confirmLabel: 'Eliminar',
        destructive: true,
        onConfirm: async () => {
          try {
            await api.delete(`/products/${p.id}`)
            toast.success('Producto eliminado')
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['product-categories'] })
          } catch (error) {
            toast.error(
              error instanceof ApiRequestError && error.code === 'in_use'
                ? 'Este producto ya se usó en comprobantes o cotizaciones: no se puede eliminar, pero sí archivar.'
                : error instanceof Error
                  ? error.message
                  : 'No se pudo eliminar el producto',
            )
          }
        },
      }),
    [queryClient],
  )

  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Producto',
        cell: ({ row }) => {
          const p = row.original
          return (
            <div className="flex items-center gap-3">
              <div
                className={
                  p.kind === 'service'
                    ? 'flex size-9 shrink-0 items-center justify-center rounded-lg bg-info-soft text-info'
                    : 'flex size-9 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success'
                }
              >
                {p.kind === 'service' ? <WrenchIcon className="size-4" /> : <BoxIcon className="size-4" />}
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium">{p.name}</div>
                <div className="truncate text-muted-foreground text-xs">
                  {p.code ? <span className="font-mono">{p.code}</span> : 'Sin código'}
                  {p.categoryName && ` · ${p.categoryName}`}
                </div>
              </div>
            </div>
          )
        },
      }),
      col.accessor('cabysCode', {
        header: 'CABYS',
        meta: { hideOnMobile: true },
        cell: ({ row }) =>
          row.original.cabysCode ? (
            <Tooltip content={row.original.cabysDescription ?? ''}>
              <span className="font-mono text-[13px]">{row.original.cabysCode}</span>
            </Tooltip>
          ) : (
            <Badge variant="warning">Falta CABYS</Badge>
          ),
      }),
      col.accessor('unit', {
        header: 'Unidad',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
      }),
      col.accessor('ivaCode', {
        header: 'IVA',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <Badge variant="outline">{IVA_RATES.find((r) => r.code === getValue())?.rate ?? 0}%</Badge>
        ),
      }),
      col.accessor('price', {
        header: 'Precio',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">
              {formatMoney(row.original.price, row.original.currency as 'CRC')}
            </div>
            {row.original.priceIncludesTax && (
              <div className="text-muted-foreground text-xs">IVA incluido</div>
            )}
            <ExtraPrices product={row.original} />
          </div>
        ),
      }),
      col.display({
        id: 'actions',
        header: '',
        meta: { align: 'right', className: 'w-12' },
        cell: ({ row }) =>
          canWrite ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon-sm" aria-label="Acciones">
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  onSelect={() => {
                    setEditing(row.original)
                    setFormOpen(true)
                  }}
                >
                  <PencilIcon /> Editar
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() =>
                    setActive.mutate(
                      { id: row.original.id, isActive: !row.original.isActive },
                      {
                        onSuccess: () =>
                          toast.success(row.original.isActive ? 'Producto archivado' : 'Producto restaurado'),
                      },
                    )
                  }
                >
                  {row.original.isActive ? <ArchiveIcon /> : <ArchiveRestoreIcon />}
                  {row.original.isActive ? 'Archivar' : 'Restaurar'}
                </DropdownMenuItem>
                <DropdownMenuItem destructive onSelect={() => removeProduct(row.original)}>
                  <Trash2Icon /> Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      }),
    ],
    [canWrite, setActive.mutate, removeProduct],
  )

  return (
    <Page>
      <PageHeader
        title="Productos y servicios"
        description="Tu catálogo con CABYS, unidad y tarifa de IVA listos para facturar."
        actions={
          <>
            <ImportExportMenu
              exportPath={`/products/export.xlsx?${exportParams}`}
              exportFilename="productos.xlsx"
              templatePath="/products/import/template.xlsx"
              templateFilename="plantilla-productos.xlsx"
              onImport={canWrite ? () => setImportOpen(true) : undefined}
            />
            {canWrite && (
              <>
                <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
                  <TagsIcon /> Categorías
                </Button>
                <Button
                  onClick={() => {
                    setEditing(null)
                    setFormOpen(true)
                  }}
                >
                  <PlusIcon /> Nuevo producto
                </Button>
              </>
            )}
          </>
        }
      />
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(p) => p.id}
        isRowMuted={(p) => !p.isActive}
        onRowClick={
          canWrite
            ? (p) => {
                setEditing(p)
                setFormOpen(true)
              }
            : undefined
        }
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Tipo"
              value={search.kind ?? 'all'}
              onValueChange={(v) =>
                setSearch({ kind: v === 'all' ? undefined : (v as 'good'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todos' },
                { value: 'good', label: 'Mercancías' },
                { value: 'service', label: 'Servicios' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por nombre o código…" />
            <FilterSelect
              label="Categoría"
              width="lg"
              value={search.category ?? 'all'}
              onValueChange={(v) => setSearch({ category: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todas las categorías' },
                ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
            <FilterSelect
              label="Activos o archivados"
              width="sm"
              value={search.status ?? 'active'}
              onValueChange={(v) =>
                setSearch({ status: v === 'active' ? undefined : (v as 'archived'), page: undefined })
              }
              options={[
                { value: 'active', label: 'Activos' },
                { value: 'archived', label: 'Archivados' },
              ]}
            />
            <ClearFilters
              active={!!(search.q || search.kind || search.category || search.status)}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  kind: undefined,
                  category: undefined,
                  status: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={BoxIcon}
            title={search.q ? 'Sin coincidencias' : 'Tu catálogo está vacío'}
            description={
              search.q
                ? `Nada coincide con “${search.q}”.`
                : 'Agregá productos o servicios con su CABYS para facturar más rápido.'
            }
            action={
              !search.q &&
              canWrite && (
                <Button onClick={() => setFormOpen(true)}>
                  <PlusIcon /> Nuevo producto
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <ProductImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <ProductFormSheet open={formOpen} onOpenChange={setFormOpen} product={editing} />
      <CategoriesDialog
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        title="Categorías de producto"
        description="Agrupan tu catálogo para filtrar y reportar. Al eliminar una, sus productos quedan sin categoría."
        endpoint="/product-categories"
        queryKey={['product-categories']}
        alsoInvalidate={[['products']]}
        canWrite={canWrite}
        usageNoun={{ one: 'producto', many: 'productos' }}
        toCategory={(c: Category) => ({ id: c.id, name: c.name, count: c.productCount })}
      />
    </Page>
  )
}

/** Precios 2–4 en una línea discreta bajo el precio base; el detalle completo va en el tooltip. */
function ExtraPrices({ product }: { product: Product }) {
  const extra = ([2, 3, 4] as const).flatMap((tier) => {
    const value = tierPrice(product, tier)
    return value === null ? [] : [{ tier, value }]
  })
  if (!extra.length) return null
  return (
    <Tooltip
      content={
        <div className="grid gap-0.5">
          {extra.map((e) => (
            <div key={e.tier} className="flex justify-between gap-4 tabular">
              <span className="opacity-70">Precio {e.tier}</span>
              {formatMoney(e.value, product.currency as 'CRC')}
            </div>
          ))}
        </div>
      }
    >
      <span className="cursor-default text-muted-foreground text-xs">
        +{extra.length} {extra.length === 1 ? 'precio' : 'precios'}
      </span>
    </Tooltip>
  )
}
